import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  AlertTitle
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

interface RefreshConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  peripheralType: 'chamber' | 'cameras';
  raspiId: string;
  hasRunningExperiment: boolean;
  runningExperimentInfo?: {
    exp_id: string;
    test_id: string;
    profile_id: string;
  };
}

const RefreshConfirmationDialog: React.FC<RefreshConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  peripheralType,
  raspiId,
  hasRunningExperiment,
  runningExperimentInfo
}) => {
  const peripheralName = peripheralType === 'chamber' ? 'Chamber' : 'Cameras';
  const peripheralNamePlural = peripheralType === 'chamber' ? 'Chamber' : 'Cameras';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon sx={{ color: 'warning.main' }} />
        Refresh {peripheralNamePlural} Confirmation
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Are you sure you want to refresh the {peripheralName.toLowerCase()} on RASPI <strong>{raspiId}</strong>?
        </Typography>
        
        {hasRunningExperiment && runningExperimentInfo && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>⚠️ WARNING: Running Experiment Will Be Stopped</AlertTitle>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>This action will stop the currently running experiment:</strong>
            </Typography>
            <Box sx={{ mt: 1, pl: 2 }}>
              <Typography variant="body2">
                • <strong>Experiment ID:</strong> {runningExperimentInfo.exp_id}
              </Typography>
              <Typography variant="body2">
                • <strong>Test ID:</strong> {runningExperimentInfo.test_id}
              </Typography>
              <Typography variant="body2">
                • <strong>Profile ID:</strong> {runningExperimentInfo.profile_id}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
              All experiment data and progress will be lost!
            </Typography>
          </Alert>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This action will:
        </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 2 }}>
          <Typography component="li" variant="body2">
            Re-discover {peripheralName.toLowerCase()} hardware connections
          </Typography>
          <Typography component="li" variant="body2">
            Update {peripheralName.toLowerCase()} status and configuration
          </Typography>
          {hasRunningExperiment && (
            <Typography component="li" variant="body2" color="error.main" fontWeight="bold">
              Stop any running experiment and lose all progress
            </Typography>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          color={hasRunningExperiment ? "error" : "primary"}
          autoFocus
        >
          {hasRunningExperiment ? 'Stop Experiment & Refresh' : `Refresh ${peripheralNamePlural}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefreshConfirmationDialog; 